import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "./supabase";

// ---------------------------------------------------------------
// Simple mobile-only auth using our own mobile_users table.
// NO Supabase Auth / No email involved = no rate limits.
// Session is stored in localStorage under the key "khasim_mobile_user".
// ---------------------------------------------------------------

const SESSION_KEY = "khasim_mobile_user";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load session from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        setUser(JSON.parse(stored));
      }
    } catch {
      localStorage.removeItem(SESSION_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------
  // Mobile-only login (OTP-less, no Supabase Auth).
  // 1️⃣ Check if phone already exists in mobile_users.
  // 2️⃣ If yes → load that record and set session.
  // 3️⃣ If no  → insert a new row and set session.
  // ---------------------------------------------------------------
  const mobileLogin = async (phone) => {
    const clean = phone.replace(/\D/g, "");
    if (clean.length !== 10) {
      throw new Error("Please enter a valid 10-digit mobile number.");
    }

    // 1️⃣ Check if user already exists
    const { data: existing, error: fetchError } = await supabase
      .from("mobile_users")
      .select("*")
      .eq("phone_number", clean)
      .maybeSingle();

    if (fetchError) throw fetchError;

    let sessionUser;

    if (existing) {
      // Returning user – reuse the existing record
      sessionUser = existing;
    } else {
      // New user – insert a row (no Supabase Auth, no email sent)
      const { data: inserted, error: insertError } = await supabase
        .from("mobile_users")
        .insert({ phone_number: clean })
        .select()
        .single();

      if (insertError) throw insertError;
      sessionUser = inserted;
    }

    // Save session locally
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionUser));
    setUser(sessionUser);
  };

  // ---------------------------------------------------------------
  // Admin login – uses Supabase Auth (email + password)
  // ---------------------------------------------------------------
  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    const authUser = data?.user;
    if (authUser) {
      setUser(authUser);
    }
    return authUser;
  };

  // ---------------------------------------------------------------
  // Logout – clears localStorage, Supabase session, and state
  // ---------------------------------------------------------------
  const logout = async () => {
    localStorage.removeItem(SESSION_KEY);
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, mobileLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  if (detail?.message) return detail.message;
  return String(detail);
}
